import React, { useEffect, useRef, useState, ReactNode } from "react";
import { DropdownSubmenu } from "react-bootstrap-submenu";
import { usePathname, useSearchParams } from "next/navigation";

const HoverDropdownSubmenu: React.FC<{
    title: ReactNode;
    href?: string;
    children: React.ReactNode;
}> = ({ title, href, children }) => {
    const [show, setShow] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const hideTimeout = useRef<NodeJS.Timeout | null>(null);
    const ignoreHoverRef = useRef(false); // Lock to prevent immediate re-opening
    const pathname = usePathname();
    const searchParams = useSearchParams();

    // Close dropdown when pathname or search params change (navigation occurred)
    useEffect(() => {
        setShow(false);
        // Lock hover events for a short duration to prevent accidental re-opening
        ignoreHoverRef.current = true;

        // Only blur when focus is inside this dropdown, so route changes do not
        // steal focus from page-level inputs (e.g. scanner textbox).
        const activeElement = document.activeElement;
        if (
            activeElement instanceof HTMLElement &&
            dropdownRef.current?.contains(activeElement)
        ) {
            activeElement.blur();
        }

        const timer = setTimeout(() => {
            ignoreHoverRef.current = false;
        }, 500);

        return () => clearTimeout(timer);
    }, [pathname, searchParams]);

    const handleMouseEnter = () => {
        if (ignoreHoverRef.current) return;

        if (hideTimeout.current) {
            clearTimeout(hideTimeout.current);
            hideTimeout.current = null;
        }
        setShow(true);
    };

    const handleMouseLeave = () => {
        // Clear any existing timeout
        if (hideTimeout.current) {
            clearTimeout(hideTimeout.current);
        }

        hideTimeout.current = setTimeout(() => {
            setShow(false);
        }, 200); // 200ms delay; adjust as needed
    };

    useEffect(() => {
        return () => {
            if (hideTimeout.current) {
                clearTimeout(hideTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                // Clear any lingering timeouts before closing
                if (hideTimeout.current) {
                    clearTimeout(hideTimeout.current);
                    hideTimeout.current = null;
                }
                setShow(false);
            }
        };

        const handleClickInside = (event: MouseEvent | TouchEvent) => {
            // Close dropdown when clicking on dropdown items or links
            const target = event.target as HTMLElement;
            if (
                dropdownRef.current &&
                dropdownRef.current.contains(target) &&
                (target.closest(".dropdown-item") ||
                    target.closest("a[href]") ||
                    target.classList.contains("dropdown-item"))
            ) {
                // Clear timeout and close immediately on item click
                if (hideTimeout.current) {
                    clearTimeout(hideTimeout.current);
                    hideTimeout.current = null;
                }
                setShow(false);
            }
        };

        // Always add event listeners to prevent conflicts and memory leaks
        // Use capture phase (true) to ensure we catch clicks even if propagation is stopped by children
        document.addEventListener("mousedown", handleClickOutside, true);
        document.addEventListener("touchstart", handleClickOutside, true);
        document.addEventListener("click", handleClickInside, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside, true);
            document.removeEventListener(
                "touchstart",
                handleClickOutside,
                true
            );
            document.removeEventListener("click", handleClickInside, true);
        };
    }, []); // Empty dependency array to prevent size changes

    return (
        <div
            ref={dropdownRef}
            className="dropdown-submenu-container"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            tabIndex={-1}
        >
            <DropdownSubmenu
                title={title as string}
                href={href}
                show={show}
                tabIndex={-1}
                className="parent-menu"
            >
                {children}
            </DropdownSubmenu>
        </div>
    );
};

export default HoverDropdownSubmenu;
